import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useState } from "react";
import { useEffect } from "react";
import { addNewIntent, createWithAi, deleteIntent, editIntent, generateWithAi } from "../../services/intents";
import ActionBody from "./components/ActionBody";
import { validateInput } from "../../utils/utils";
import RoundedButton from "../buttons/RoundedButton";
import FormDialog from "../dialog/FormDialog";
import DeleteDialog from "../dialog/DeleteDialog";
import InfoSection from "../info/InfoSection";
import TextAreaWithCounter from "../inputs/TextAreaWithCounter";
import { showInfoToast } from "../../utils/toast";
import DeleteButton from "../buttons/DeleteButton";
import NoDataMessage from "../cards/NoDataMessage";
import AlertMsg from "../info/AlertMsg";
import { BiLink, BiUnlink, BiChevronDown, BiChevronUp } from "react-icons/bi";
import { FiEdit2 } from "react-icons/fi";
import { MdDelete } from "react-icons/md";
import ConfirmDialog from "../dialog/ConfirmDialog";
import { Checkbox } from "primereact/checkbox";
import AddButton from "../buttons/AddButton";
import { FiEdit } from "react-icons/fi";
import NoDataMsg from "../info/NoDataMsg";

const emptyIntent = {
  name: "",
  description: ""
};

const MIN_INTENT_DESCRIPTION_LENGTH = 20;
const MAX_INTENT_DESCRIPTION_LENGTH = 100;
const MAX_URL_DISPLAY_LENGTH = 45;

const isValidCompanyURL = (url = "") => {
  try {
    const parsedURL = new URL(url);
    return ["http:", "https:"].includes(parsedURL.protocol) && !!parsedURL.hostname;
  } catch {
    return false;
  }
};

const truncateURL = (url = "", maxLength = MAX_URL_DISPLAY_LENGTH) => {
  if (url.length <= maxLength) {
    return url;
  }

  return `${url.slice(0, maxLength)}...`;
};

function TableIntents({ intents, reloadTable }) {
  const [intentsData, setIntentsData] = useState([]);
  const [intentDialog, setIntentDialog] = useState(false);

  const [intentGenerateDialog, setIntentGenerateDialog] = useState(false);
  const [companyURLs, setCompanyURLs] = useState([]);
  const [companyURLInput, setCompanyURLInput] = useState("");

  const [aiResultsDialog, setAiResultsDialog] = useState(false);
  const [aiGeneratedIntents, setAiGeneratedIntents] = useState([]);
  const [expandedGeneratedIntentIds, setExpandedGeneratedIntentIds] = useState(new Set());
  const [selectedGeneratedIntentIds, setSelectedGeneratedIntentIds] = useState(new Set());
  const [editGeneratedIntentDialog, setEditGeneratedIntentDialog] = useState(false);
  const [generatedIntentToEdit, setGeneratedIntentToEdit] = useState(null);

  const [deleteIntentDialog, setDeleteIntentDialog] = useState(false);
  const [editIntentDialog, setEditIntentDialog] = useState(false);
  const [newIntent, setNewIntent] = useState(emptyIntent);
  const [intent, setIntent] = useState({});

  const [isProcessing, setIsProcessing] = useState(false);
  const [isSavingIntents, setIsSavingIntents] = useState(false);

  useEffect(() => {
    setIntentsData(intents ? [...intents] : []);
  }, [intents]);

  const isDescriptionLengthValid = (description = "") => {
    const length = description.trim().length;
    return (
      length >= MIN_INTENT_DESCRIPTION_LENGTH &&
      length <= MAX_INTENT_DESCRIPTION_LENGTH
    );
  };

  const saveIntent = async () => {
    if (newIntent.name.trim() !== "" && newIntent.description.trim() !== "") {
      if (!isDescriptionLengthValid(newIntent.description)) {
        showInfoToast("La descripción debe tener entre 20 y 100 caracteres.");
        return;
      }
      setIntentDialog(false);
      const res = await addNewIntent(newIntent);
      if (res) {
        setNewIntent(emptyIntent);
        await reloadTable();
      }
    } else {
      showInfoToast("Por favor, completa todos los campos requeridos.");
    }
  };

  const saveEditIntent = async () => {
    if (!isDescriptionLengthValid(intent.description || "")) {
      showInfoToast("La descripción debe tener entre 20 y 100 caracteres.");
      return;
    }
    setEditIntentDialog(false);
    const res = await editIntent(intent);
    if (res) {
      setIntent({});
      await reloadTable();
    }
    await reloadTable();
  };

  const openNew = () => {
    setNewIntent(emptyIntent);
    setIntentDialog(true);
  };

  const openGenerateWithAiDialog = () => {
    setCompanyURLs([]);
    setCompanyURLInput("");
    setIntentGenerateDialog(true);
  };

  const handleGenerateWithAi = async () => {
    setIsProcessing(true);
    const result = await generateWithAi(companyURLs);
    setIsProcessing(false);
    if (result) {
      setIntentGenerateDialog(false);
      const normalizedIntents = result.map((generatedIntent, index) => ({
        ...generatedIntent,
        tempId: `${generatedIntent.name || generatedIntent.title || "intent"}-${index}`,
      }));
      setAiGeneratedIntents(normalizedIntents);
      setExpandedGeneratedIntentIds(new Set());
      setSelectedGeneratedIntentIds(new Set());
      setGeneratedIntentToEdit(null);
      setEditGeneratedIntentDialog(false);
      setAiResultsDialog(true);
    }
  };

  const getGeneratedIntentId = (generatedIntent = {}) => {
    return generatedIntent.tempId || generatedIntent.name || generatedIntent.title || "";
  };

  const toggleGeneratedIntentSelection = (generatedIntent) => {
    const id = getGeneratedIntentId(generatedIntent);
    if (!id) {
      return;
    }

    setSelectedGeneratedIntentIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const openEditGeneratedIntentDialog = (generatedIntent) => {
    const generatedIntentId = getGeneratedIntentId(generatedIntent);
    setGeneratedIntentToEdit({
      ...generatedIntent,
      tempId: generatedIntentId,
      examples:
        Array.isArray(generatedIntent.examples) && generatedIntent.examples.length > 0
          ? generatedIntent.examples
          : [""],
    });
    setEditGeneratedIntentDialog(true);
  };

  const updateGeneratedIntentExample = (index, value) => {
    setGeneratedIntentToEdit((prev) => {
      const nextExamples = [...(prev.examples || [])];
      nextExamples[index] = value;
      return {
        ...prev,
        examples: nextExamples,
      };
    });
  };

  const addGeneratedIntentExample = () => {
    setGeneratedIntentToEdit((prev) => ({
      ...prev,
      examples: ["", ...(prev.examples || [])],
    }));
  };

  const removeGeneratedIntentExample = (indexToRemove) => {
    setGeneratedIntentToEdit((prev) => {
      const nextExamples = (prev.examples || []).filter(
        (_, index) => index !== indexToRemove
      );
      return {
        ...prev,
        examples: nextExamples.length > 0 ? nextExamples : [""],
      };
    });
  };

  const saveEditedGeneratedIntent = () => {
    if (!generatedIntentToEdit) {
      return;
    }

    const title = (generatedIntentToEdit.title || "").trim();
    const description = (generatedIntentToEdit.description || "").trim();

    if (!title || !description) {
      showInfoToast("El título y la descripción son obligatorios.");
      return;
    }

    const examples = (generatedIntentToEdit.examples || [])
      .map((example) => example.trim())
      .filter(Boolean);

    setAiGeneratedIntents((prev) =>
      prev.map((generatedIntent) =>
        getGeneratedIntentId(generatedIntent) === generatedIntentToEdit.tempId
          ? {
            ...generatedIntent,
            title: generatedIntentToEdit.title,
            description: generatedIntentToEdit.description,
            response: generatedIntentToEdit.response,
            suggestion: generatedIntentToEdit.suggestion,
            examples,
          }
          : generatedIntent
      )
    );

    setEditGeneratedIntentDialog(false);
    setGeneratedIntentToEdit(null);
  };

  const saveSelectedGeneratedIntents = async () => {
    const intentsToSave = aiGeneratedIntents.filter((generatedIntent) =>
      selectedGeneratedIntentIds.has(getGeneratedIntentId(generatedIntent))
    );

    if (intentsToSave.length === 0) {
      showInfoToast("Selecciona al menos una intención para guardar.");
      return;
    }

    setIsSavingIntents(true);
    const res = await createWithAi(intentsToSave);
    setIsSavingIntents(false);

    if (res) {
      await reloadTable();
      setAiResultsDialog(false);
      setSelectedGeneratedIntentIds(new Set());
      return;
    }

    showInfoToast("No se pudieron guardar las intenciones seleccionadas.");
  };

  const toggleIntentExpanded = (intentId) => {
    setExpandedGeneratedIntentIds((prev) => {
      const next = new Set(prev);
      if (next.has(intentId)) {
        next.delete(intentId);
      } else {
        next.add(intentId);
      }
      return next;
    });
  };

  const addCompanyURL = () => {
    const trimmedURL = companyURLInput.trim();

    if (!trimmedURL) {
      showInfoToast("Ingresa una URL para agregarla a la lista.");
      return;
    }

    const alreadyExists = companyURLs.some(
      (url) => url.toLowerCase() === trimmedURL.toLowerCase()
    );

    if (alreadyExists) {
      showInfoToast("La URL ya fue agregada.");
      return;
    }

    setCompanyURLs((prevURLs) => [...prevURLs, trimmedURL]);
    setCompanyURLInput("");
  };

  const removeCompanyURL = (urlToRemove) => {
    setCompanyURLs((prevURLs) =>
      prevURLs.filter((url) => url !== urlToRemove)
    );
  };

  const openDeleteDialog = (rowData) => {
    setDeleteIntentDialog(!deleteIntentDialog);
    setIntent(rowData);
  };

  const openEditDialog = (rowData) => {
    setEditIntentDialog(!editIntentDialog);
    setIntent(rowData);
  };

  const deleteSelectedIntent = async () => {
    setDeleteIntentDialog(false);
    const res = await deleteIntent(intent);
    if (res) {
      await reloadTable();
    }
  };
  return (
    <>
      {intentsData && intentsData.length > 0 ? (
        <div className="card p-0" aria-label="table"
          style={{
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <DataTable
            value={intentsData}
            dataKey="id"
            paginator
            rows={10}
            rowsPerPageOptions={[5, 10, 25, 50]}
            filterDisplay="row"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords}"
            filter
            responsiveLayout="scroll"

          >
            <Column
              field="title"
              className={"Normal Table"}
              header={"Intenciones"}
              filter
              filterMatchMode={["contains"]}
              showFilterMenu={false}
              body={(rowData) => (
                <div className="d-flex flex-column gap-1">
                  <span>{rowData.title}</span>
                  <small className="text-muted">{rowData.description || "- Sin descripción -"}</small>
                </div>
              )}
            />
            <Column
              field="qa_count"
              className={"Normal Table"}
              header={"Cantidad de ejemplos"}
              filter
              filterMatchMode={["contains"]}
              showFilterMenu={false}
              body={(rowData) => (
                <div className="d-flex flex-column gap-1">
                  <div>
                  {rowData.qa_count > 0 ? (
                    <span className="entity-values-badge">
                      {rowData.qa_count} ejemplo{rowData.qa_count > 1 ? "s" : ""}
                    </span>
                  ) : (
                    <span className="text-muted"><i>- Sin ejemplos -</i> </span>
                  )}
                  </div>
                </div>
              )}
            />
            <Column
              header={
                <div className="d-flex gap-1">
                  <RoundedButton label="+ Agregar" handleClick={openNew} />
                  <RoundedButton label="Generar con IA" handleClick={openGenerateWithAiDialog} color="info" />
                </div>
              }
              className={"actionheader"}
              bodyClassName="table-intents-actions-cell"
              body={(rowData) => {
                return (
                  <ActionBody
                    data={rowData}
                    toggleDelete={openDeleteDialog}
                    toggleEdit={openEditDialog}
                    editB={true}
                    deleteB={true}
                    containerClassName="table-intents-actions"
                  />
                );
              }}
              exportable={false}
            ></Column>
          </DataTable>
        </div>
      ) : (
        <NoDataMsg
          icon={<FiEdit size={40} />}
          title="No hay intenciones registradas"
          subtitle="Comienza creando una nueva intención o genera sugerencias con IA."
          buttons={[
            <RoundedButton key="add-intent-empty" label="+ Agregar" handleClick={openNew} />,
            <RoundedButton key="ai-intent-empty" label="Generar con IA" handleClick={openGenerateWithAiDialog} color="info" />,
          ]}
        />
      )}

      <FormDialog
        visible={intentDialog}
        title="Crear Nueva Intención"
        closeDialog={() => {
          setIntentDialog(false);
        }}
        acceptDialog={saveIntent}
      >
        <div className="mb-3">
          <InfoSection
            title="IMPORTANTE"
            initialShowState={true}
          >
            <span>
              Si necesitas más información, puedes visitar nuestro blog para
              entender mejor el funcionamiento de las intenciones. <br />
              <a
                href="https://trinitalkbot.com/consejos-para-crear-respuestas-principales-efectivas/"
                target="_blank"
                rel="noreferrer"
                style={{ color: "#034fa5", fontWeight: "bold" }}
              >
                Consejos para crear respuestas principales efectivas
              </a>
            </span>
          </InfoSection>
        </div>

        <div className="d-flex flex-column gap-3">
          <div className="field">
            <label className={"ms-3"} htmlFor="newIntent">
              Nombre de Intención
            </label>
            <input
              value={newIntent.name}
              onChange={(e) => {
                if (validateInput("text", e.target.value)) {
                  setNewIntent({ ...newIntent, name: e.target.value });
                }
              }}
              required
              maxLength={100}
              placeholder="Ingresa un nombre de intención.."
            />
          </div>
          <div className="field">
            <label className={"ms-3"} htmlFor="newIntentDescription">
              Descripción de Intención
            </label>
            <TextAreaWithCounter
              value={newIntent.description}
              onChange={(e) => {
                if (validateInput("text", e.target.value)) {
                  setNewIntent({ ...newIntent, description: e.target.value });
                }
              }}
              required
              maxLength={100}
              placeholder="Ingresa una descripción de intención.."
            />
          </div>
        </div>
      </FormDialog>

      <FormDialog
        visible={editIntentDialog}
        title="Editar Intención"
        closeDialog={() => {
          setEditIntentDialog(false);
        }}
        acceptDialog={saveEditIntent}
      >
        <div className="mb-3">
          <InfoSection
            title="IMPORTANTE"
            initialShowState={true}
          >
            <span>
              Si necesitas más información, puedes visitar nuestro blog para
              entender mejor el funcionamiento de las intenciones. <br />
              <a
                href="https://trinitalkbot.com/consejos-para-crear-respuestas-principales-efectivas/"
                target="_blank"
                rel="noreferrer"
                style={{ color: "#034fa5", fontWeight: "bold" }}
              >
                Consejos para crear respuestas principales efectivas
              </a>
            </span>
          </InfoSection>
        </div>
        <div className="d-flex flex-column mt-3 gap-3">
          <div className="field">
            <label className={"ms-3"} htmlFor="intentName">
              Nombre de Intención
            </label>
            <input
              value={intent.title}
              disabled
              style={{ cursor: "not-allowed" }}
              title="El nombre de la intención no se puede editar."
            />
          </div>
          <div className="field">
            <label className={"ms-3"} htmlFor="intentDescription">
              Descripción de Intención
            </label>
            <TextAreaWithCounter
              value={intent.description || ""}
              onChange={(e) => {
                if (validateInput("text", e.target.value)) {
                  setIntent({ ...intent, description: e.target.value });
                }
              }}
              required
              maxLength={100}
              placeholder="Ingresa una descripción de intención.."
            />
          </div>
        </div>
      </FormDialog>

      <FormDialog
        visible={intentGenerateDialog}
        width="500px"
        title="Generar Intenciones con IA"
        closeDialog={() => {
          setIntentGenerateDialog(false);
        }}
        acceptDialog={handleGenerateWithAi}
        acceptText={isProcessing ? "Generando..." : "Generar"}
        isAcceptDisabled={companyURLs.length === 0 || !companyURLs.some(isValidCompanyURL) || isProcessing}
      >
        <AlertMsg text={"Esta funcionalidad hace uso del conector de OpenAI configurado. Se aplicarán costos adicionales por cada consulta."} />
        <AlertMsg text={"Esta funcionalidad solo esta disponible para <strong>modelos GPT-5 en adelante</strong>. Asegúrate de configurar un modelo compatible."} type="danger" />
        <div className="d-flex flex-column mt-3 gap-3">
          <div className="field p-3 card-secondary">
            <label className={"ms-3"} htmlFor="companyURLInput">
              URL del sitio
            </label>
            <div className="d-flex gap-2 align-items-start flex-wrap">
              <input
                id="companyURLInput"
                type="url"
                value={companyURLInput}
                onChange={(e) => {
                  setCompanyURLInput(e.target.value);
                }}
                placeholder="https://empresa.com"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addCompanyURL();
                  }
                }}
              />
              <div className="col-12 djaCenter mt-2 mb-1">
                <RoundedButton label="Agregar" handleClick={addCompanyURL} />
              </div>
            </div>
          </div>

          <div className="d-flex flex-column gap-2">
            {companyURLs.length > 0 ? (
              companyURLs.map((url) => {
                const isValidURL = isValidCompanyURL(url);

                return (
                  <div
                    key={url}
                    className="d-flex justify-content-between align-items-center gap-2 ps-3 pe-2 py-2"
                    style={{
                      border: `1px solid ${isValidURL ? "#d9d9d9" : "#e66a6a"}`,
                      borderRadius: "10px",
                      color: isValidURL ? "inherit" : "#c0392b",
                    }}
                  >
                    <div className="d-flex align-items-center gap-2">
                      {isValidURL ? <BiLink /> : <BiUnlink color="#c0392b" />}
                      <span title={url} style={{ wordBreak: "break-all" }}>
                        {truncateURL(url)}
                      </span>
                    </div>
                    <DeleteButton onClick={() => removeCompanyURL(url)} />
                  </div>
                );
              })
            ) : (
              <></>
            )}
          </div>
        </div>
      </FormDialog>

      <FormDialog
        visible={aiResultsDialog}
        width="600px"
        title="Intenciones generadas con IA"
        closeDialog={() => {
          setAiResultsDialog(false);
        }}
        footer={
          <>
            <RoundedButton
              label="Cancelar"
              color="secondary"
              handleClick={() => setAiResultsDialog(false)}
            />
            <RoundedButton
              label={isSavingIntents ? "Guardando..." : "Guardar"}
              handleClick={saveSelectedGeneratedIntents}
              color="success"
              disabled={selectedGeneratedIntentIds.size === 0 || isSavingIntents}
            />
          </>
        }
      >
        <div className="mb-3">
          <AlertMsg
            type="info"
            text={`La inteligencia artificial identificó <strong>${aiGeneratedIntents.length} posible${aiGeneratedIntents.length === 1 ? "" : "s"} intenci${aiGeneratedIntents.length === 1 ? "ón" : "ones"}.</strong> Revísa${aiGeneratedIntents.length === 1 ? "la" : "las"} y edíta${aiGeneratedIntents.length === 1 ? "la" : "las"} si es necesario antes de guardarla${aiGeneratedIntents.length === 1 ? "" : "s"}.`}
          />
          <AlertMsg
            type="warning"
            text="Recuerda que luego de guardar las intenciones, deberás <strong>reentrenar el modelo para que los cambios tengan efecto.</strong>"
          />
        </div>
        <div>
          {aiGeneratedIntents.length === 0 ? (
            <NoDataMessage text="No se encontraron intenciones generadas." />
          ) : (
            <>
              <label className="ms-2 mb-2">Selecciona las intenciones que deseas guardar:</label>
              <div className="d-flex flex-column gap-2" >
                {aiGeneratedIntents.map((generatedIntent) => {
                  const generatedIntentId = getGeneratedIntentId(generatedIntent);
                  const isExpanded = expandedGeneratedIntentIds.has(generatedIntentId);
                  const isChecked = selectedGeneratedIntentIds.has(generatedIntentId);

                  return (
                    <div
                      key={generatedIntentId || generatedIntent.title}
                      style={{
                        border: "1px solid #d9d9d9",
                        borderRadius: "10px",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        className="d-flex justify-content-between align-items-center gap-2 p-3"
                        style={{ cursor: "pointer" }}
                        onClick={() => toggleIntentExpanded(generatedIntentId)}
                      >
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          <Checkbox
                            checked={isChecked}
                            onChange={() => toggleGeneratedIntentSelection(generatedIntent)}
                          />
                        </div>
                        <div className="d-flex flex-column gap-1" style={{ flex: 1 }}>
                          <div className="d-flex align-items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <span style={{ fontWeight: 600 }}>{generatedIntent.title}</span>
                            <button
                              type="button"
                              onClick={() => openEditGeneratedIntentDialog(generatedIntent)}
                              style={{
                                border: "none",
                                background: "transparent",
                                display: "flex",
                                alignItems: "center",
                                padding: 0,
                                color: "#1568b1",
                                cursor: "pointer",
                                fontSize: "0.85rem"
                              }}
                              aria-label="edit-generated-intent-title"
                              title="Editar intención generada"
                            >
                              <FiEdit size={12} /> Editar
                            </button>
                          </div>
                          <small className="text-muted">{generatedIntent.description}</small>
                        </div>
                        <div className="d-flex align-items-center gap-2 flex-shrink-0">
                          <small className="text-muted">{generatedIntent.examples.length} ejemplos</small>
                          {isExpanded ? <BiChevronUp /> : <BiChevronDown />}
                        </div>
                      </div>

                      {isExpanded && (
                        <div
                          style={{
                            borderTop: "1px solid #eee",
                            padding: "12px 16px",
                            background: "#fafafa",
                          }}
                        >
                          <div className="d-flex flex-column gap-3">
                            <div>
                              <small style={{ fontWeight: 600, color: "#666" }}>RESPUESTA</small>
                              <p className="mt-1 mb-0" style={{ fontSize: "0.9rem" }}>{generatedIntent.response}</p>
                            </div>
                            <div>
                              <small style={{ fontWeight: 600, color: "#666" }}>EJEMPLOS</small>
                              {generatedIntent.examples && (
                                <ul className="mt-1 mb-0 ps-3" style={{ fontSize: "0.85rem", color: "#444" }}>
                                  {generatedIntent.examples.length > 0 ? (
                                    generatedIntent.examples.map((example, idx) => (
                                      <li key={idx}>{example}</li>
                                    ))
                                  ) : (
                                    <li className="text-muted"><i>Sin ejemplos</i></li>
                                  )}
                                </ul>
                              )}

                            </div>
                            <div>
                              <small style={{ fontWeight: 600, color: "#666" }}>SUGERENCIAS</small>
                              <p>{generatedIntent.suggestion}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </FormDialog>

      <FormDialog
        visible={editGeneratedIntentDialog}
        width="600px"
        title="Editar intención generada"
        closeDialog={() => {
          setEditGeneratedIntentDialog(false);
          setGeneratedIntentToEdit(null);
        }}
        acceptDialog={saveEditedGeneratedIntent}
        acceptText="Guardar cambios"
      >
        {generatedIntentToEdit && (
          <div className="d-flex flex-column gap-3">
            <div className="field">
              <label className="ms-3">Título</label>
              <input
                value={generatedIntentToEdit.title || ""}
                onChange={(e) =>
                  setGeneratedIntentToEdit((prev) => ({
                    ...prev,
                    title: e.target.value,
                  }))
                }
                maxLength={100}
                placeholder="Ingresa el título de la intención.."
              />
            </div>

            <div className="field">
              <label className="ms-3">Descripción</label>
              <TextAreaWithCounter
                value={generatedIntentToEdit.description || ""}
                onChange={(e) =>
                  setGeneratedIntentToEdit((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                maxLength={200}
                placeholder="Ingresa una descripción.."
              />
            </div>

            <div className="field">
              <label className="ms-3">Respuesta</label>
              <TextAreaWithCounter
                value={generatedIntentToEdit.response || ""}
                onChange={(e) =>
                  setGeneratedIntentToEdit((prev) => ({
                    ...prev,
                    response: e.target.value,
                  }))
                }
                maxLength={1000}
                placeholder="Ingresa la respuesta.."
              />
            </div>

            <div className="field">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-2">
                  <label className="ms-3">Ejemplos</label>
                  <span className="entity-values-badge">
                    {generatedIntentToEdit.examples.length} ejemplo{generatedIntentToEdit.examples.length > 1 ? "s" : ""}
                  </span>
                </div>
                <div className="me-3">
                  <AddButton
                    onClick={() => {
                      addGeneratedIntentExample();
                    }}
                  />
                </div>

              </div>

              <div className="d-flex flex-column gap-2 mt-2 py-1 px-2" style={{ maxHeight: "280px", overflowY: "auto" }}>
                {(generatedIntentToEdit.examples || [""]).map((example, index) => (
                  <div key={`example-${index}`} className="d-flex align-items-center gap-2">
                    <input
                      value={example}
                      onChange={(e) => updateGeneratedIntentExample(index, e.target.value)}
                      placeholder={`Ejemplo ${index + 1}`}
                      maxLength={200}
                    />
                    <DeleteButton onClick={() => removeGeneratedIntentExample(index)} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </FormDialog>

      <ConfirmDialog
        visible={deleteIntentDialog}
        title="Eliminar Intención"
        onClose={() => {
          setDeleteIntentDialog(false);
        }}
        icon={<MdDelete size={40} />}
        iconBackgroundColor="red"
        confirmColor="danger"
        onConfirm={deleteSelectedIntent}
        description="¿Estás seguro que quieres eliminar la intención y sus valores asociados?"
      />
    </>
  );
}

export default TableIntents;
